import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

/** Tilt-shift: blur grows with distance from a horizontal focus band (miniature look). */
const TiltShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    delta: { value: new THREE.Vector2(1, 0) },
    focus: { value: 0.5 },
    band: { value: 0.24 },
    amount: { value: 1.4 },
    resolution: { value: new THREE.Vector2(1, 1) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 delta;
    uniform float focus;
    uniform float band;
    uniform float amount;
    uniform vec2 resolution;
    #ifdef TO_SCREEN
    uniform sampler2D tSharp;
    #endif
    varying vec2 vUv;
    void main() {
      float d = max(0.0, abs(vUv.y - focus) - band);
      float r = clamp(d * amount * 4.0, 0.0, 1.0) * 3.0;
      vec4 c;
      #if defined(FINAL)
      // Both blurs are done: the sharp band reads the scene, the rest the blurred picture (smoothed up from half size).
      if (r <= 0.0) c = texture2D(tSharp, vUv);
      else c = texture2D(tDiffuse, vUv);
      #else
      #ifdef TO_SCREEN
      if (r <= 0.0) c = texture2D(tSharp, vUv);
      #else
      if (r <= 0.0) c = texture2D(tDiffuse, vUv);
      #endif
      else {
      vec2 px = delta / resolution * r;
      c = texture2D(tDiffuse, vUv) * 0.2270270270;
      c += texture2D(tDiffuse, vUv + px * 1.3846153846) * 0.3162162162;
      c += texture2D(tDiffuse, vUv - px * 1.3846153846) * 0.3162162162;
      c += texture2D(tDiffuse, vUv + px * 3.2307692308) * 0.0702702703;
      c += texture2D(tDiffuse, vUv - px * 3.2307692308) * 0.0702702703;
      }
      #endif
      gl_FragColor = c;
      #ifdef TO_SCREEN
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
      #endif
    }`,
};


/** Rows past the sharp band the vertical taps can reach (3.23 × the widest blur of 3 texels, rounded up). */
const REACH = 12;

/**
 * The miniature look: the scene into a multisampled half-float target, a horizontal blur into a plain one, and
 * the vertical blur straight to the screen with tone mapping and the output color space (what OutputPass did, in
 * the same pass). The sharp band in the middle reads the scene as it is: no blur taps and no horizontal pass there.
 * Same picture as the composer it replaces (scene, two blur passes, OutputPass), in one full-screen pass less
 * and with no multisampling on the blur targets.
 */
export class Post {
  private sharp: THREE.WebGLRenderTarget | null = null;
  private blur: THREE.WebGLRenderTarget | null = null;
  /** The vertical blur, at half size too (see setSize). */
  private blur2: THREE.WebGLRenderTarget | null = null;
  private h: FullScreenQuad | null = null;
  private v: FullScreenQuad | null = null;
  /** To the screen: the sharp band or the blurred picture, tone mapped. */
  private out: FullScreenQuad | null = null;
  enabled: boolean;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private camera: THREE.Camera,
    enabled: boolean,
  ) {
    this.enabled = enabled;
  }

  private pass(dx: number, dy: number, toScreen: boolean): FullScreenQuad {
    const m = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(TiltShader.uniforms),
      vertexShader: TiltShader.vertexShader,
      fragmentShader: TiltShader.fragmentShader,
      defines: toScreen ? { TO_SCREEN: '', FINAL: '' } : {},
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
      toneMapped: toScreen,
    });
    (m.uniforms.delta as { value: THREE.Vector2 }).value.set(dx, dy);
    if (toScreen) m.uniforms.tSharp = { value: null };
    return new FullScreenQuad(m);
  }

  private build(): void {
    // The scene and the blurs in packed floats (R11F_G11F_B10F): 32 bits a pixel instead of half floats' 64, the same
    // range above 1 for the tone mapping at the end, no alpha (the scene has none to keep). Half the memory moved
    // drawing, blending (the fog's four layers), resolving the multisampling and blurring: the frame ~17% less at
    // 1922×911 in SwiftShader; next to the half floats the picture differs by under 1/255 on average. Drawing into
    // it needs EXT_color_buffer_float (some phones only have the half-float one): without it, half floats as before.
    const packed = !!this.renderer.getContext().getExtension('EXT_color_buffer_float') && (this.renderer.getContext() as WebGL2RenderingContext).R11F_G11F_B10F !== undefined;
    const fmt = packed ? { type: THREE.HalfFloatType, format: THREE.RGBFormat, internalFormat: 'R11F_G11F_B10F' as const } : { type: THREE.HalfFloatType };
    this.sharp = new THREE.WebGLRenderTarget(1, 1, { ...fmt, samples: 4 });
    this.blur = new THREE.WebGLRenderTarget(1, 1, { ...fmt, depthBuffer: false });
    this.blur2 = new THREE.WebGLRenderTarget(1, 1, { ...fmt, depthBuffer: false });
    this.h = this.pass(1, 0, false);
    this.v = this.pass(0, 1, false);
    this.out = this.pass(0, 0, true);
    const s = this.renderer.getSize(new THREE.Vector2());
    this.setSize(s.x, s.y, this.renderer.getPixelRatio());
  }

  dispose(): void {
    this.sharp?.dispose();
    this.blur?.dispose();
    this.blur2?.dispose();
    for (const q of [this.h, this.v, this.out]) {
      q?.material.dispose();
      q?.dispose();
    }
    this.sharp = this.blur = this.blur2 = this.h = this.v = this.out = null;
  }

  setSize(w: number, h: number, pr: number): void {
    if (!this.sharp || !this.blur || !this.blur2 || !this.h || !this.v || !this.out) return;
    const pw = Math.floor(w * pr);
    const ph = Math.floor(h * pr);
    this.sharp.setSize(pw, ph);
    // Both blurs at half the size each way: a quarter of their pixels and of what each reads, and the pass to the
    // screen reads one texel (the blurred picture smoothed up, or the sharp band from the scene itself, untouched).
    // The taps reach as far as before (offsets in the screen's texels). On a real bastion the blurred bands differ by
    // under 1/255 on average from full size. A weak GPU's biggest cost after the scene (an Intel HD 4600 at
    // 1922×911, client-perf 2026-10-31).
    for (const t of [this.blur, this.blur2]) t.setSize(Math.max(1, Math.ceil(pw / 2)), Math.max(1, Math.ceil(ph / 2)));
    for (const q of [this.h, this.v, this.out]) ((q.material as THREE.ShaderMaterial).uniforms.resolution as { value: THREE.Vector2 }).value.set(w * pr, h * pr);
  }

  /**
   * Compiles the scene's shaders (or only `part`, lit as the scene lights it) for the target it is drawn into
   * (three builds a different program for the screen, with tone mapping, than for the sharp target). Settles once
   * the new programs are linked and three has checked them, so a chain of these paces the GPU instead of piling up.
   *
   * Without KHR_parallel_shader_compile (most Android phones), three's compileAsync settles after 10 ms without
   * waiting, and the link wait lands where three first uses the program (its first drawing): a frame of a few
   * hundred ms with nothing new in it. Here a fence tells when the browser's GPU process got past the links, without
   * blocking, and only then is each program used (its uniforms read), which then costs nothing. With the extension
   * (desktops), ANGLE links on a thread of its own that the fence doesn't wait for: each program is also asked
   * whether it finished (`isReady`, COMPLETION_STATUS_KHR) before it is used.
   */
  compile(part?: THREE.Object3D): Promise<unknown> {
    const r = this.renderer;
    if (this.enabled && !this.sharp) this.build();
    const before = new Set(r.info.programs ?? []);
    r.setRenderTarget(this.enabled ? this.sharp : null);
    const materials = r.compile(part ?? this.scene, this.camera, part ? this.scene : null);
    r.setRenderTarget(null);
    const fresh = [...materials]
      .map((m) => (r.properties.get(m) as { currentProgram?: Program }).currentProgram)
      .filter((p): p is Program => !!p && !before.has(p as never));
    if (!fresh.length) return Promise.resolve();
    return linked(r.getContext())
      .then(() => ready(r.getContext(), fresh))
      .then(() => {
        for (const p of fresh) p.getUniforms();
      });
  }

  render(): void {
    if (!this.enabled) {
      this.renderer.render(this.scene, this.camera);
      return;
    }
    if (!this.sharp) this.build();
    const r = this.renderer;
    const h = this.h as FullScreenQuad;
    const v = this.v as FullScreenQuad;
    r.setRenderTarget(this.sharp);
    r.render(this.scene, this.camera);
    ((h.material as THREE.ShaderMaterial).uniforms.tDiffuse as { value: THREE.Texture | null }).value = (this.sharp as THREE.WebGLRenderTarget).texture;
    // The sharp band reads the scene itself: the horizontal blur only fills the rows above and below it, plus
    // the reach of the vertical taps just outside the band.
    const blur = this.blur as THREE.WebGLRenderTarget;
    const u = (h.material as THREE.ShaderMaterial).uniforms;
    const focus = (u.focus as { value: number }).value;
    const band = (u.band as { value: number }).value;
    const ph = blur.height;
    const lo = Math.min(ph, Math.floor((focus - band) * ph) + REACH);
    const hi = Math.max(0, Math.ceil((focus + band) * ph) - REACH);
    const blur2 = this.blur2 as THREE.WebGLRenderTarget;
    ((v.material as THREE.ShaderMaterial).uniforms.tDiffuse as { value: THREE.Texture | null }).value = blur.texture;
    // The same rows for both blurs: the vertical one reads the horizontal one's.
    for (const [t, q] of [[blur, h], [blur2, v]] as const) {
      t.scissorTest = true;
      for (const [y0, y1] of [[0, lo], [hi, ph]] as const) {
        if (y1 <= y0) continue;
        t.scissor.set(0, y0, t.width, y1 - y0);
        r.setRenderTarget(t);
        q.render(r);
      }
    }
    const ou = ((this.out as FullScreenQuad).material as THREE.ShaderMaterial).uniforms;
    (ou.tDiffuse as { value: THREE.Texture | null }).value = blur2.texture;
    (ou.tSharp as { value: THREE.Texture | null }).value = (this.sharp as THREE.WebGLRenderTarget).texture;
    r.setRenderTarget(null);
    (this.out as FullScreenQuad).render(r);
  }
}

type Program = { getUniforms(): unknown; isReady(): boolean };

/** Settles once every program says it finished linking (always at once without KHR_parallel_shader_compile). */
const ready = (gl: WebGLRenderingContext | WebGL2RenderingContext, programs: Program[]): Promise<void> =>
  new Promise((resolve) => {
    const check = (): void => {
      if (gl.isContextLost() || programs.every((p) => p.isReady())) resolve();
      else setTimeout(check, 8);
    };
    check();
  });

/** Settles once the GPU has run everything sent so far (links included), checked without blocking. */
const linked = (gl: WebGLRenderingContext | WebGL2RenderingContext): Promise<void> => {
  const g = gl as WebGL2RenderingContext;
  const sync = typeof g.fenceSync === 'function' ? g.fenceSync(g.SYNC_GPU_COMMANDS_COMPLETE, 0) : null;
  if (!sync) return Promise.resolve();
  g.flush();
  return new Promise((resolve) => {
    const check = (): void => {
      if (g.isContextLost() || g.getSyncParameter(sync, g.SYNC_STATUS) === g.SIGNALED) {
        g.deleteSync(sync);
        resolve();
      } else setTimeout(check, 8);
    };
    setTimeout(check, 8);
  });
};
